'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import HeroSetupForm, { isSetupComplete } from './HeroSetupForm';

type ProfileKey = 'theo' | 'tracy';

interface ProfileState {
  setupDone: boolean;
}

export default function ProfileCards() {
  const router = useRouter();
  const [theoSetup, setTheoSetup] = useState<ProfileState>({ setupDone: false });
  const [tracySetup, setTracySetup] = useState<ProfileState>({ setupDone: false });
  const [activeSetup, setActiveSetup] = useState<ProfileKey | null>(null);

  useEffect(() => {
    setTheoSetup({ setupDone: isSetupComplete('theo') });
    setTracySetup({ setupDone: isSetupComplete('tracy') });
  }, []);

  function handleProfileClick(profile: ProfileKey) {
    const done = profile === 'theo' ? theoSetup.setupDone : tracySetup.setupDone;
    if (!done) {
      setActiveSetup(profile);
    } else {
      router.push(`/profile-dashboard?profile=${profile}`);
    }
  }

  function handleSetupComplete(profile: ProfileKey) {
    if (profile === 'theo') setTheoSetup({ setupDone: true });
    else setTracySetup({ setupDone: true });
    setActiveSetup(null);
    router.push(`/profile-dashboard?profile=${profile}`);
  }

  return (
    <>
      {activeSetup === 'theo' && (
        <HeroSetupForm
          profileKey="theo"
          profileName="Theo"
          accentClass="primary"
          borderClass="border-primary"
          onComplete={() => handleSetupComplete('theo')}
          onCancel={() => setActiveSetup(null)}
        />
      )}
      {activeSetup === 'tracy' && (
        <HeroSetupForm
          profileKey="tracy"
          profileName="Tracy"
          accentClass="accent"
          borderClass="border-accent"
          onComplete={() => handleSetupComplete('tracy')}
          onCancel={() => setActiveSetup(null)}
        />
      )}

      <div className="flex flex-col sm:flex-row gap-6 mb-14 w-full max-w-lg">
        {/* Theo */}
        <button
          type="button"
          onClick={() => handleProfileClick('theo')}
          className="group flex-1 card-forge profile-theo border-2 rounded-xl p-8 flex flex-col items-center gap-4 hover:glow-green transition-all duration-300 hover:scale-[1.02] cursor-pointer text-left"
        >
          <div className="w-16 h-16 rounded-full bg-primary/10 border-2 border-primary/40 flex items-center justify-center">
            <span className="text-2xl font-black text-primary">T</span>
          </div>
          <div className="text-center w-full">
            <div className="text-forge-label mb-1">Profile 01</div>
            <div className="text-2xl font-black tracking-widest text-foreground">THEO</div>
            {theoSetup.setupDone ? (
              <>
                <div className="text-xs text-muted-foreground mt-2 font-medium">Forge Score Today</div>
                <div className="text-3xl font-black text-primary font-tabular mt-1">—</div>
              </>
            ) : (
              <div className="mt-2 inline-flex items-center gap-1.5 bg-primary/10 border border-primary/30 rounded-full px-3 py-1">
                <span className="text-xs text-primary font-bold tracking-wider">SETUP REQUIRED</span>
              </div>
            )}
          </div>
          {theoSetup.setupDone && (
            <div className="w-full pt-3 border-t border-border">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Status</span>
                <span className="text-primary font-bold">Active</span>
              </div>
            </div>
          )}
          <div className="btn-forge-primary w-full text-center py-2.5 rounded-md text-sm">
            {theoSetup.setupDone ? 'ENTER →' : 'BEGIN SETUP →'}
          </div>
        </button>

        {/* Tracy */}
        <button
          type="button"
          onClick={() => handleProfileClick('tracy')}
          className="group flex-1 card-forge profile-tracy border-2 rounded-xl p-8 flex flex-col items-center gap-4 hover:glow-amber transition-all duration-300 hover:scale-[1.02] cursor-pointer text-left"
        >
          <div className="w-16 h-16 rounded-full bg-accent/10 border-2 border-accent/40 flex items-center justify-center">
            <span className="text-2xl font-black text-accent">T</span>
          </div>
          <div className="text-center w-full">
            <div className="text-forge-label mb-1">Profile 02</div>
            <div className="text-2xl font-black tracking-widest text-foreground">TRACY</div>
            {tracySetup.setupDone ? (
              <>
                <div className="text-xs text-muted-foreground mt-2 font-medium">Forge Score Today</div>
                <div className="text-3xl font-black text-accent font-tabular mt-1">—</div>
              </>
            ) : (
              <div className="mt-2 inline-flex items-center gap-1.5 bg-accent/10 border border-accent/30 rounded-full px-3 py-1">
                <span className="text-xs text-accent font-bold tracking-wider">SETUP REQUIRED</span>
              </div>
            )}
          </div>
          {tracySetup.setupDone && (
            <div className="w-full pt-3 border-t border-border">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Status</span>
                <span className="text-accent font-bold">Active</span>
              </div>
            </div>
          )}
          <div className="w-full text-center py-2.5 rounded-md text-sm font-bold tracking-widest uppercase border border-accent/40 text-accent bg-accent/10 hover:bg-accent/20 transition-all">
            {tracySetup.setupDone ? 'ENTER →' : 'BEGIN SETUP →'}
          </div>
        </button>
      </div>
    </>
  );
}